import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Upload, X, Package, Save, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiUtils } from '../utils/api';

const ProductForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [images, setImages] = useState([]);
  const [primaryImageId, setPrimaryImageId] = useState(null);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [imageError, setImageError] = useState('');
  const [productDetails, setProductDetails] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset
  } = useForm({
    defaultValues: {
      name: '',
      description: '',
      price: '',
      unit: 'bag',
      category: '',
      remark: '',
      sku: '',
      isActive: true,
      inStock: true,
      netWeight: '',
      modeOfTransportation: '',
      importedCountry: ''
    }
  });

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchProduct();
    }
  }, [id, isEditing]);

  const fetchCategories = async () => {
    const response = await apiUtils.fetchWithPagination('/categories', {
      limit: 100,
      sortBy: 'name',
      sortOrder: 'asc'
    });

    if (response.success) {
      setCategories(response.data.filter(cat => cat.isActive));
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    const response = await apiUtils.fetchOne(`/products/${id}`);

    if (response.success) {
      const product = response.data;

      // Set form values
      reset({
        name: product.name,
        description: product.description || '',
        price: (product.price === 'Not Available' || product.price === 'N/A') ? 0 : product.price,
        unit: product.unit,
        category: product.category?._id || '',
        remark: product.remark || '',
        sku: product.sku || '',
        isActive: product.isActive,
        inStock: product.inStock,
        netWeight: product.netWeight || 0,
        modeOfTransportation: product.modeOfTransportation || '',
        importedCountry: product.importedCountry || ''
      });

      // Set images if exists
      if (product.images && product.images.length > 0) {
        setImages(product.images.map(img => ({
          _id: img._id,
          url: img.url,
          publicId: img.publicId,
          filename: img.filename,
          size: img.size,
          mimetype: img.mimetype,
          isPrimary: img.isPrimary,
          preview: img.url || img.fullUrl
        })));
        const primary = product.images.find(img => img.isPrimary);
        if (primary) {
          setPrimaryImageId(primary._id || primary.publicId);
        }
      } else if (product.image && (product.image.url || product.imageUrl)) {
        // Fallback for old single image - convert to new format for display
        const legacyImage = {
          _id: product.image._id || `temp-${Date.now()}`,
          url: product.image.url || product.imageUrl,
          publicId: product.image.publicId,
          filename: product.image.filename,
          size: product.image.size,
          mimetype: product.image.mimetype,
          isPrimary: true,
          preview: product.image.url || product.imageUrl
        };
        setImages([legacyImage]);
        setPrimaryImageId(legacyImage._id);
      }

      // Set product details if exists
      if (product.productDetails && Array.isArray(product.productDetails)) {
        setProductDetails(product.productDetails);
      }
    } else {
      toast.error('Failed to fetch product details');
      navigate('/products');
    }

    setLoading(false);
  };

  const addProductDetail = () => {
    setProductDetails([...productDetails, '']);
  };

  const removeProductDetail = (index) => {
    const newDetails = productDetails.filter((_, i) => i !== index);
    setProductDetails(newDetails);
  };

  const updateProductDetail = (index, value) => {
    const newDetails = [...productDetails];
    newDetails[index] = value;
    setProductDetails(newDetails);
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Clear any previous image error
    setImageError('');

    // Validate total number of images
    if (images.length + files.length > 10) {
      toast.error('Maximum 10 images allowed');
      return;
    }

    const newImages = [];

    files.forEach(file => {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not a valid image file`);
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 5MB`);
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          _id: `new-${Date.now()}-${Math.random()}`,
          file: file,
          preview: e.target.result,
          isPrimary: images.length === 0 && newImages.length === 0,
          isNew: true
        };

        newImages.push(newImage);

        if (newImages.length === files.length) {
          setImages(prev => [...prev, ...newImages]);

          // Set first image as primary if no primary exists
          if (!primaryImageId && newImages.length > 0) {
            setPrimaryImageId(newImages[0]._id);
          }
        }
      };
      reader.readAsDataURL(file);
    });

    // Reset file input
    e.target.value = '';
  };

  const removeImage = (imageId) => {
    const imageToRemove = images.find(img =>
      img._id === imageId ||
      img.publicId === imageId ||
      (img._id && img._id.toString() === imageId) ||
      (img.publicId && img.publicId.toString() === imageId)
    );

    if (imageToRemove && !imageToRemove.isNew) {
      // Mark existing image for deletion - use publicId for Cloudinary deletion
      const idToRemove = imageToRemove.publicId || imageToRemove._id;
      setRemovedImageIds(prev => [...prev, idToRemove]);
    }

    // Filter out the image - must match either _id or publicId
    const filteredImages = images.filter(img => {
      const matches = img._id === imageId ||
        img.publicId === imageId ||
        (img._id && img._id.toString() === imageId) ||
        (img.publicId && img.publicId.toString() === imageId);
      return !matches;
    });

    setImages(filteredImages);

    // If removed image was primary, set first remaining image as primary
    const wasPrimary = primaryImageId === imageId ||
      primaryImageId === imageToRemove?._id ||
      primaryImageId === imageToRemove?.publicId;

    if (wasPrimary) {
      if (filteredImages.length > 0) {
        const newPrimaryId = filteredImages[0]._id || filteredImages[0].publicId;
        setPrimaryImageId(newPrimaryId);
      } else {
        setPrimaryImageId(null);
      }
    }
  };

  const setPrimaryImage = (imageId) => {
    setPrimaryImageId(imageId);
    setImages(prev => prev.map(img => ({
      ...img,
      isPrimary: (img._id === imageId || img.publicId === imageId)
    })));
  };

  const onSubmit = async (data) => {
    // Validate that at least one image exists
    const newImageFiles = images.filter(img => img.isNew && img.file);
    const existingImagesCount = images.filter(img => {
      const imageId = img._id || img.publicId;
      return !img.isNew &&
        imageId &&
        !imageId.toString().startsWith('temp-') &&
        !removedImageIds.includes(imageId) &&
        !(img.publicId && removedImageIds.includes(img.publicId));
    }).length;

    const totalImages = newImageFiles.length + existingImagesCount;

    if (totalImages === 0) {
      setImageError('At least one product image is required');
      // Scroll to the image section
      document.getElementById('images-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // Clear image error if validation passes
    setImageError('');

    setSubmitting(true);

    try {
      // Prepare form data
      const formData = new FormData();

      // Add text fields
      Object.keys(data).forEach(key => {
        formData.append(key, data[key]);
      });

      // Add product details
      formData.append('productDetails', JSON.stringify(productDetails.filter(detail => detail.trim() !== '')));

      // Add new image files and track which one is primary
      const newImageFiles = images.filter(img => img.isNew && img.file);
      let newImagePrimaryIndex = -1;

      newImageFiles.forEach((img, index) => {
        formData.append('images', img.file);
        // Check if this new image is the primary one
        if (img._id === primaryImageId || img.publicId === primaryImageId) {
          newImagePrimaryIndex = index;
        }
      });

      // Send the index of which new image is primary
      if (newImagePrimaryIndex >= 0) {
        formData.append('newImagePrimaryIndex', newImagePrimaryIndex);
      }

      // Add existing images (not new uploads and not removed)
      // Only include images that are:
      // 1. Not new uploads (isNew = false or undefined)
      // 2. Have a valid identifier (_id or publicId, excluding temp IDs)
      // 3. Not in the removedImageIds list
      const existingImages = images
        .filter(img => {
          // Skip new images
          if (img.isNew) return false;

          // Get the image identifier (_id or publicId)
          const imageId = img._id || img.publicId;

          // Skip if no identifier
          if (!imageId) return false;

          // Skip temp IDs
          if (typeof imageId === 'string' && imageId.startsWith('temp-')) return false;

          // Skip if image is in removed list
          if (removedImageIds.includes(imageId)) return false;

          // Also check publicId in removed list
          if (img.publicId && removedImageIds.includes(img.publicId)) return false;

          return true;
        })
        .map(img => ({
          _id: img._id,
          url: img.url,
          publicId: img.publicId,
          filename: img.filename,
          size: img.size,
          mimetype: img.mimetype,
          isPrimary: img._id === primaryImageId || img.publicId === primaryImageId
        }));

      if (existingImages.length > 0) {
        formData.append('existingImages', JSON.stringify(existingImages));
      }

      // For old single image products being edited, let backend handle migration automatically
      // No need to send empty existingImages

      // Add primary image ID
      if (primaryImageId) {
        formData.append('primaryImageId', primaryImageId);
      }

      // Add removed image IDs
      if (removedImageIds.length > 0) {
        formData.append('removedImageIds', JSON.stringify(removedImageIds));
      }


      // Make API call
      const endpoint = isEditing ? `/products/${id}` : '/products';
      const method = isEditing ? 'update' : 'create';

      const response = await apiUtils[method](endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.success) {
        toast.success(`Product ${isEditing ? 'updated' : 'created'} successfully`);
        navigate('/products');
      } else {
        toast.error(response.error || `Failed to ${isEditing ? 'update' : 'create'} product`);
      }
    } catch (error) {
      console.error('Product form error:', error);
      toast.error('An unexpected error occurred');
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" text="Loading product..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/products')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Product' : 'Add New Product'}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {isEditing ? 'Update product information' : 'Create a new fruit product'}
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Product Name *</label>
                    <input
                      {...register('name', {
                        required: 'Product name is required',
                        minLength: { value: 2, message: 'Name must be at least 2 characters' },
                        maxLength: { value: 100, message: 'Name cannot exceed 100 characters' }
                      })}
                      type="text"
                      className={`form-input ${errors.name ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="e.g., Fresh Red Apples"
                    />
                    {errors.name && <p className="form-error">{errors.name.message}</p>}
                  </div>

                  <div>
                    <label className="form-label">SKU</label>
                    <input
                      {...register('sku', {
                        maxLength: { value: 20, message: 'SKU cannot exceed 20 characters' }
                      })}
                      type="text"
                      className="form-input"
                      placeholder="Auto-generated if empty"
                    />
                    {errors.sku && <p className="form-error">{errors.sku.message}</p>}
                  </div>
                </div>

                <div>
                  <label className="form-label">Category *</label>
                  <select
                    {...register('category', { required: 'Category is required' })}
                    className={`form-select ${errors.category ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  >
                    <option value="">Select a category</option>
                    {categories.map(category => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="form-error">{errors.category.message}</p>}
                </div>

                <div>
                  <label className="form-label">Description</label>
                  <textarea
                    {...register('description', {
                      maxLength: { value: 1000, message: 'Description cannot exceed 1000 characters' }
                    })}
                    rows={3}
                    className={`form-textarea ${errors.description ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    placeholder="Describe the product..."
                  />
                  {errors.description && <p className="form-error">{errors.description.message}</p>}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="form-label mb-0">Product Details</label>
                    <button
                      type="button"
                      onClick={addProductDetail}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-lg hover:bg-brand-100 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Detail
                    </button>
                  </div>
                  <div className="space-y-2">
                    {productDetails.map((detail, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={detail}
                          onChange={(e) => updateProductDetail(index, e.target.value)}
                          className="form-input flex-1"
                          placeholder="Enter product detail..."
                        />
                        <button
                          type="button"
                          onClick={() => removeProductDetail(index)}
                          className="p-2 text-red-600 hover:bg-red-50 border border-gray-300 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Add key features and highlights of the product
                  </p>
                </div>

                <div>
                  <label className="form-label">Remark</label>
                  <textarea
                    {...register('remark', {
                      maxLength: { value: 500, message: 'Remark cannot exceed 500 characters' }
                    })}
                    rows={2}
                    className="form-textarea"
                    placeholder="Additional notes or remarks..."
                  />
                  {errors.remark && <p className="form-error">{errors.remark.message}</p>}
                </div>
              </div>
            </div>

            {/* Pricing & Inventory */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Pricing & Inventory</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="form-label">Price *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">AED</span>
                      <input
                        {...register('price', {
                          required: 'Price is required',
                          min: { value: 0, message: 'Price must be positive' },
                          pattern: {
                            value: /^\d+(\.\d{1,2})?$/,
                            message: 'Please enter a valid price (up to 2 decimal places)'
                          }
                        })}
                        type="number"
                        step="0.01"
                        min="0"
                        className={`form-input pl-12 ${errors.price ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                        placeholder="0.00"
                      />
                    </div>
                    {errors.price && <p className="form-error">{errors.price.message}</p>}
                  </div>

                  <div>
                    <label className="form-label">Unit *</label>
                    <select
                      {...register('unit', { required: 'Unit is required' })}
                      className={`form-select ${errors.unit ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="bag">Bag</option>
                      <option value="box">Box</option>
                    </select>
                    {errors.unit && <p className="form-error">{errors.unit.message}</p>}
                  </div>

                  <div>
                    <label className="form-label">Net Weight (kg) *</label>
                    <input
                      {...register('netWeight', {
                        required: 'Net weight is required',
                        min: { value: 0, message: 'Net weight cannot be negative' }
                      })}
                      type="number"
                      step="0.01"
                      min="0"
                      className={`form-input ${errors.netWeight ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                      placeholder="0.00"
                    />
                    {errors.netWeight && <p className="form-error">{errors.netWeight.message}</p>}
                  </div>
                </div>
              </div>
            </div>

            {/* Transportation & Origin */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Transportation & Origin</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="form-label">Mode of Transportation *</label>
                    <select
                      {...register('modeOfTransportation', { required: 'Mode of transportation is required' })}
                      className={`form-select ${errors.modeOfTransportation ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select transportation mode</option>
                      <option value="Air/Sea">Air/Sea</option>
                      <option value="Air">Air</option>
                      <option value="Sea">Sea</option>
                    </select>
                    {errors.modeOfTransportation && <p className="form-error">{errors.modeOfTransportation.message}</p>}
                  </div>

                  <div>
                    <label className="form-label">Imported Country *</label>
                    <select
                      {...register('importedCountry', { required: 'Imported country is required' })}
                      className={`form-select ${errors.importedCountry ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                    >
                      <option value="">Select country</option>
                      <option value="India">India</option>
                      <option value="China">China</option>
                      <option value="USA">USA</option>
                      <option value="Brazil">Brazil</option>
                      <option value="Turkey">Turkey</option>
                      <option value="Chile">Chile</option>
                      <option value="South Africa">South Africa</option>
                      <option value="Argentina">Argentina</option>
                      <option value="New Zealand">New Zealand</option>
                      <option value="Egypt">Egypt</option>
                      <option value="Peru">Peru</option>
                      <option value="Morocco">Morocco</option>
                      <option value="Spain">Spain</option>
                      <option value="Italy">Italy</option>
                      <option value="Greece">Greece</option>
                      <option value="Israel">Israel</option>
                      <option value="Mexico">Mexico</option>
                      <option value="Ecuador">Ecuador</option>
                      <option value="Philippines">Philippines</option>
                      <option value="Thailand">Thailand</option>
                      <option value="Vietnam">Vietnam</option>
                      <option value="Pakistan">Pakistan</option>
                      <option value="Iran">Iran</option>
                      <option value="Afghanistan">Afghanistan</option>
                      <option value="Uganda">Uganda</option>
                      <option value="Other">Other</option>
                    </select>
                    {errors.importedCountry && <p className="form-error">{errors.importedCountry.message}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Product Images */}
            <div className="card" id="images-section">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Product Images *</h3>
                <p className="text-sm text-gray-500 mt-1">Upload up to 10 images. Click on an image to set it as primary.</p>
              </div>
              <div className="card-body">
                {/* Image Grid */}
                {images.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    {images.map((image, index) => (
                      <div
                        key={image._id || image.publicId || index}
                        className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-all ${(image._id === primaryImageId || image.publicId === primaryImageId)
                            ? 'border-brand-500 ring-2 ring-brand-500'
                            : 'border-gray-300 hover:border-brand-300'
                          }`}
                        onClick={() => setPrimaryImage(image._id || image.publicId)}
                      >
                        <img
                          src={image.preview || image.url}
                          alt={`Product ${index + 1}`}
                          className="w-full h-32 object-cover"
                        />

                        {/* Primary Badge */}
                        {(image._id === primaryImageId || image.publicId === primaryImageId) && (
                          <div className="absolute top-2 left-2 bg-brand-600 text-white text-xs px-2 py-1 rounded-full font-medium">
                            Primary
                          </div>
                        )}

                        {/* Remove Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(image._id || image.publicId);
                          }}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                {images.length < 10 && (
                  <div>
                    <label htmlFor="images" className={`btn-secondary w-full inline-flex items-center justify-center cursor-pointer ${imageError ? 'border-red-300' : ''}`}>
                      <Upload className="h-4 w-4 mr-2" />
                      {images.length > 0 ? 'Add More Images' : 'Upload Images'}
                    </label>
                    <input
                      id="images"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImagesChange}
                      className="hidden"
                    />
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      PNG, JPG, GIF up to 5MB each ({images.length}/10)
                    </p>
                  </div>
                )}

                {/* Image Error Message */}
                {imageError && (
                  <p className="form-error mt-2">{imageError}</p>
                )}
              </div>
            </div>

            {/* Status Settings */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold text-gray-900">Status</h3>
              </div>
              <div className="card-body space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Active</label>
                  <input
                    {...register('isActive')}
                    type="checkbox"
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">In Stock</label>
                  <input
                    {...register('inStock')}
                    type="checkbox"
                    className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full inline-flex items-center justify-center"
              >
                {submitting ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isEditing ? 'Update Product' : 'Create Product'}
              </button>

              <button
                type="button"
                onClick={() => navigate('/products')}
                className="btn-ghost w-full"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;